import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Paper,
  Grid,
  Stack,
  Card,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { startOfMonth, startOfDay, startOfWeek, endOfWeek, endOfMonth, addMonths, subMonths, addWeeks } from "date-fns";
import { useAuth } from "../../auth/hooks/useAuth";
import { useClientProfile } from "../../auth/hooks/useClientProfile";
import { useOrganization } from "../../auth/hooks/useOrganization";
import { useClientReportExercise } from "../hooks/useClientReportExercise";
import { useClientReportHealth } from "../hooks/useClientReportHealth";
import { useCurrentWorkout } from "../hooks/useCurrentWorkout";
import { useProgressInsights } from "../hooks/useProgressInsights";
import { getCalendarDataQueryRange } from "../../../lib/dateUtils";
import { readStoredCalendarViewMode, writeStoredCalendarViewMode } from "../../../lib/calendarPrefs";
import { ROUTES } from "../../../constants";
import MonthCalendar from "../components/MonthCalendar";
import CalendarLegend from "../components/CalendarLegend";
import DayReport from "../components/DayReport";
import ProgressInsightsSection from "../components/ProgressInsightsSection";
import DashboardPageHeader from "../components/DashboardPageHeader";
import WorkoutsChartsPanel from "../components/WorkoutsChartsPanel";
import RecentWorkoutSessionsList from "../components/RecentWorkoutSessionsList";
import ExerciseProgressExplorer from "../components/ExerciseProgressExplorer";
import WeeklyReportsPanel from "../components/WeeklyReportsPanel";
import HealthSummaryPanel from "../components/HealthSummaryPanel";

const TAB_ACTIVITY = 0;
const TAB_HEALTH = 1;
const TAB_WORKOUTS = 2;
const TAB_WEEKLY = 3;

const TRAINING_OVERVIEW = 0;
const TRAINING_HISTORY = 1;

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ width: "100%" }}>{children}</Box>;
}

const DashboardPage = () => {
  const navigate = useNavigate();
  const { clientId, organizationId } = useAuth();
  const { client } = useClientProfile();
  const { weightUnit } = useOrganization(organizationId);
  const [mainTab, setMainTab] = useState(TAB_ACTIVITY);
  const [trainingSubTab, setTrainingSubTab] = useState(TRAINING_OVERVIEW);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [calendarViewMode, setCalendarViewMode] = useState(() => readStoredCalendarViewMode());

  useEffect(() => {
    writeStoredCalendarViewMode(calendarViewMode);
  }, [calendarViewMode]);

  /** Align month anchor with whichever day is selected so month navigation stays coherent. */
  useEffect(() => {
    if (!selectedDay) return;
    setViewMonth(startOfMonth(selectedDay));
  }, [selectedDay]);

  const { start, end } = useMemo(() => {
    if (calendarViewMode === "week") {
      const anchor = selectedDay ? startOfDay(selectedDay) : startOfDay(new Date());
      const ws = startOfWeek(anchor, { weekStartsOn: 0 });
      const we = endOfWeek(anchor, { weekStartsOn: 0 });
      return {
        start: startOfMonth(ws),
        end: endOfMonth(we),
      };
    }
    return getCalendarDataQueryRange(viewMonth);
  }, [calendarViewMode, selectedDay, viewMonth]);

  const { currentWorkout } = useCurrentWorkout({ clientId, organizationId });

  const {
    metrics: progressMetrics,
    aggregated,
    workouts: workouts90d,
    loading: progressInsightsLoading,
  } = useProgressInsights({
    clientId,
    organizationId,
    days: 90,
  });

  const { workouts, loading: workoutsLoading, error: workoutsError } = useClientReportExercise({
    clientId,
    organizationId,
    start,
    end,
  });

  const { healthReports, loading: healthLoading, error: healthError } = useClientReportHealth({
    clientId,
    organizationId,
    start,
    end,
  });

  const loading = workoutsLoading || healthLoading;
  const error = workoutsError || healthError;

  const handleChangeMonth = (delta) => {
    setViewMonth((prev) => (delta > 0 ? addMonths(prev, 1) : subMonths(prev, 1)));
    setSelectedDay(null);
  };

  const handleNavigateWeek = (delta) => {
    setSelectedDay((prev) => {
      const base = prev ? startOfDay(prev) : startOfDay(new Date());
      return startOfDay(addWeeks(base, delta));
    });
  };

  return (
    <Box sx={{ maxWidth: 1440, width: "100%", mx: "auto" }}>
      <Box sx={{ mx: { xs: -1.5, sm: -2 } }}>
        <DashboardPageHeader
          title="Your progress"
          subtitle="Daily activity, health & nutrition trends, training, and weekly check-ins — aligned with what your coach sees."
          icon={<TrendingUpIcon />}
          action={
            client?.protocolId ? (
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                onClick={() => navigate(ROUTES.TRAINING_PLAN)}
                sx={{ fontWeight: 600 }}
              >
                Training plan
              </Button>
            ) : null
          }
        />
      </Box>

      <Card elevation={0} sx={{ overflow: "hidden" }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 1, sm: 2 },
            borderBottom: 1,
            borderColor: "divider",
            minHeight: 52,
            "& .MuiTab-root": { minHeight: 52, textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab icon={<CalendarTodayIcon />} iconPosition="start" label="Activity" />
          <Tab icon={<FavoriteIcon />} iconPosition="start" label="Health & nutrition" />
          <Tab icon={<FitnessCenterIcon />} iconPosition="start" label="Workouts" />
          <Tab icon={<AssignmentTurnedInIcon />} iconPosition="start" label="Weekly check-ins" />
        </Tabs>

        <TabPanel value={mainTab} index={TAB_ACTIVITY}>
          <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 3 } }}>
            {error && (
              <Alert severity="error">{error.message ?? "Failed to load data."}</Alert>
            )}
            <Box>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                Daily activity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Under each day number, colored dots mean (left→right when present): Mighty45 workout,
                health &amp; macros, Apple Health fitness — see the legend. Tap a date for detail.
              </Typography>
              <MonthCalendar
                viewMonth={viewMonth}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                onChangeMonth={handleChangeMonth}
                onWeekNavigate={handleNavigateWeek}
                viewMode={calendarViewMode}
                onViewModeChange={setCalendarViewMode}
                workouts={workouts}
                healthReports={healthReports}
                loading={loading}
              />
              <CalendarLegend />
            </Box>
            {selectedDay && (
              <DayReport
                date={selectedDay}
                workouts={workouts}
                healthReports={healthReports}
                client={client}
                organizationId={organizationId}
                clientId={clientId}
                weightUnit={weightUnit}
                aggregated={aggregated}
                loading={loading}
              />
            )}
          </Stack>
        </TabPanel>

        <TabPanel value={mainTab} index={TAB_HEALTH}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <HealthSummaryPanel
              clientId={clientId}
              organizationId={organizationId}
              weightUnit={weightUnit}
            />
          </Box>
        </TabPanel>

        <TabPanel value={mainTab} index={TAB_WORKOUTS}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Tabs
              value={trainingSubTab}
              onChange={(_, v) => setTrainingSubTab(v)}
              variant="fullWidth"
              sx={{
                mb: 3,
                borderBottom: 1,
                borderColor: "divider",
                minHeight: 48,
                "& .MuiTab-root": { minHeight: 48, textTransform: "none", fontWeight: 600 },
              }}
            >
              <Tab
                value={TRAINING_OVERVIEW}
                icon={<DashboardOutlinedIcon fontSize="small" />}
                iconPosition="start"
                label="Overview"
              />
              <Tab
                value={TRAINING_HISTORY}
                icon={<ShowChartIcon fontSize="small" />}
                iconPosition="start"
                label="History & charts"
              />
            </Tabs>

            {trainingSubTab === TRAINING_OVERVIEW && (
              <Stack spacing={3}>
                {error && (
                  <Alert severity="error">{error.message ?? "Failed to load data."}</Alert>
                )}
                <Box>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Training calendar
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Same calendar as Activity: dot colors match the legend (Mighty45, health &amp; macros,
                    Apple Health fitness). Tap a date for training + synced workouts.
                  </Typography>
                  <MonthCalendar
                    viewMonth={viewMonth}
                    selectedDay={selectedDay}
                    onSelectDay={setSelectedDay}
                    onChangeMonth={handleChangeMonth}
                    onWeekNavigate={handleNavigateWeek}
                    viewMode={calendarViewMode}
                    onViewModeChange={setCalendarViewMode}
                    workouts={workouts}
                    healthReports={healthReports}
                    loading={loading}
                  />
                  <CalendarLegend />
                </Box>
                {selectedDay && (
                  <DayReport
                    date={selectedDay}
                    workouts={workouts}
                    healthReports={healthReports}
                    client={client}
                    organizationId={organizationId}
                    clientId={clientId}
                    weightUnit={weightUnit}
                    aggregated={aggregated}
                    loading={loading}
                  />
                )}
                <Grid container spacing={3}>
                  {currentWorkout && (
                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          height: "100%",
                          borderRadius: 2,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          bgcolor: "background.paper",
                        }}
                      >
                        <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
                          Current session
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="primary" sx={{ mt: 0.5 }}>
                          {currentWorkout?.reportData?.split?.name ?? "—"}
                        </Typography>
                        {currentWorkout?.reportData?.protocol?.name && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            {currentWorkout.reportData.protocol.name}
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ mt: 2 }}
                          onClick={() => navigate(ROUTES.TRAINING_PLAN)}
                        >
                          Open in plan
                        </Button>
                      </Paper>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <ProgressInsightsSection
                      metrics={progressMetrics}
                      loading={progressInsightsLoading}
                      weightUnit={weightUnit}
                    />
                  </Grid>
                </Grid>
              </Stack>
            )}

            {trainingSubTab === TRAINING_HISTORY && (
              <Stack spacing={4}>
                <Typography variant="body2" color="text.secondary">
                  Volume and frequency use your last 90 days of completed workouts — same period as the
                  summary cards above.
                </Typography>
                <WorkoutsChartsPanel metrics={progressMetrics} weightUnit={weightUnit} />
                <ExerciseProgressExplorer
                  aggregated={aggregated}
                  exercisesWithProgression={progressMetrics?.exercisesWithProgression}
                  weightUnit={weightUnit}
                />
                <RecentWorkoutSessionsList
                  workouts={workouts90d}
                  organizationId={organizationId}
                  clientId={clientId}
                  weightUnit={weightUnit}
                  aggregated={aggregated}
                />
              </Stack>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={mainTab} index={TAB_WEEKLY}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Weekly check-ins you submit from the app are stored here. Coaches open the same reports in
              the Reports queue to review your week.
            </Typography>
            <WeeklyReportsPanel clientId={clientId} organizationId={organizationId} />
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default DashboardPage;
